import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { patientsService } from '@/services/patients';
import { getApiError } from '@/services/api';
import { WILAYAS } from '@/data/wilayas';
import { getCommunesByWilaya } from '@/data/communes';
import { COUNTRIES } from '@/data/countries';
import type { CreatePatientInput, Gender } from '@dpfs/shared';
import { PageHeader } from '@/components/ui';
import { AlertCircle } from 'lucide-react';

// Form values: same fields as the shared CreatePatientInput, with string-literal gender
// (the shared type uses the Gender enum, whose values are these same strings).
interface PatientForm {
  identityType: 'NIN' | 'PASSPORT';
  nin?: string;
  passport?: string;
  country: string;
  firstName: string;
  lastName: string;
  firstNameAr?: string;
  lastNameAr?: string;
  dateOfBirth: string;
  gender: 'MALE' | 'FEMALE';
  phone?: string;
  email?: string;
  wilayaId?: number;
  communeId?: number;
  address?: string;
}

export function NewPatient() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string[]>>({});

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<PatientForm>({
    defaultValues: {
      identityType: 'NIN',
      country: 'DZ',
      gender: 'MALE',
      wilayaId: 16,
    },
  });

  const identityType = watch('identityType');
  const isForeign = identityType === 'PASSPORT';
  const selectedWilayaId = watch('wilayaId');
  const communes = getCommunesByWilaya(Number(selectedWilayaId));

  const createMutation = useMutation({
    mutationFn: (input: PatientForm) =>
      patientsService.create({ ...input, gender: input.gender as Gender } satisfies CreatePatientInput),
    onSuccess: () => {
      navigate('/doctor/patients');
    },
    onError: (err) => {
      const apiErr = getApiError(err);
      setError(apiErr.message);
      setServerErrors(apiErr.errors || {});
    },
  });

  const onSubmit = (data: PatientForm) => {
    setError(null);
    setServerErrors({});

    // Strip identity-specific fields that don't apply, so hidden values don't leak
    const payload: PatientForm = { ...data };
    if (data.identityType === 'PASSPORT') {
      delete payload.nin;
      delete payload.wilayaId;
      delete payload.communeId;
    } else {
      delete payload.passport;
      payload.country = 'DZ';
    }
    createMutation.mutate(payload);
  };

  const ninValue = watch('nin') || '';

  const getNinInputClass = () => {
    if (hasFieldError('nin')) return 'input-error';
    if (ninValue.length === 0) return 'input';
    if (/^\d{18}$/.test(ninValue)) return 'input-success';
    return 'input-warning';
  };

  const hasFieldError = (field: keyof PatientForm) =>
    !!errors[field] || !!serverErrors[field];

  const fieldErrorMsg = (field: keyof PatientForm) =>
    errors[field]?.message || serverErrors[field]?.[0];

  const radioCard = (selected: boolean) =>
    `flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border-2 cursor-pointer text-center transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary-500 has-[:focus-visible]:ring-offset-1 ${
      selected ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 text-gray-700 hover:border-gray-300'
    }`;

  const renderFieldError = (field: keyof PatientForm) => {
    const msg = fieldErrorMsg(field);
    return msg ? <p className="field-error">{msg}</p> : null;
  };

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader
        title={t('patient.new')}
        description={t('doctor.newPatient.description')}
        backTo="/doctor/patients"
        backLabel={t('nav.patients')}
      />

      {error && (
        <div className="alert-danger mb-4" role="alert">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="break-words">{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="card">
          <h2 className="card-title mb-4">{t('doctor.newPatient.identity')}</h2>

          <div className="grid gap-4">
            <fieldset>
              <legend className="label">{t('patient.identityType')}</legend>
              <div className="flex flex-col gap-3 sm:flex-row">
                <label className={radioCard(!isForeign)}>
                  <input type="radio" value="NIN" className="sr-only" {...register('identityType')} />
                  <span className="text-sm font-medium">{t('patient.identityNin')}</span>
                </label>
                <label className={radioCard(isForeign)}>
                  <input type="radio" value="PASSPORT" className="sr-only" {...register('identityType')} />
                  <span className="text-sm font-medium">{t('patient.identityPassport')}</span>
                </label>
              </div>
            </fieldset>

            {!isForeign ? (
              <div>
                <label htmlFor="np-nin" className="label">{t('patient.nin')} *</label>
                <div className="relative">
                  <input
                    id="np-nin"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={18}
                    className={`${getNinInputClass()} pe-16 font-mono`}
                    {...register('nin', {
                      required: !isForeign ? t('common.required') : false,
                      pattern: {
                        value: /^\d{18}$/,
                        message: t('validation.ninInvalid'),
                      },
                    })}
                  />
                  {ninValue.length > 0 && (
                    <span className={`pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 text-xs font-medium tabular-nums ${/^\d{18}$/.test(ninValue) ? 'text-success-600' : 'text-warning-600'}`}>
                      {ninValue.length}/18
                    </span>
                  )}
                </div>
                {renderFieldError('nin')}
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="np-passport" className="label">{t('patient.passport')} *</label>
                  <input
                    id="np-passport"
                    type="text"
                    autoComplete="off"
                    maxLength={20}
                    className={`${hasFieldError('passport') ? 'input-error' : 'input'} font-mono`}
                    {...register('passport', {
                      required: isForeign ? t('common.required') : false,
                      pattern: {
                        value: /^[A-Za-z0-9]{5,20}$/,
                        message: t('validation.passportInvalid'),
                      },
                    })}
                  />
                  {renderFieldError('passport')}
                </div>
                <div>
                  <label htmlFor="np-country" className="label">{t('patient.country')} *</label>
                  <select
                    id="np-country"
                    className={hasFieldError('country') ? 'input-error' : 'input'}
                    {...register('country', { required: t('common.required') })}
                  >
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  {renderFieldError('country')}
                </div>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="np-lastName" className="label">{t('auth.lastName')} *</label>
                <input
                  id="np-lastName"
                  type="text"
                  autoComplete="off"
                  className={hasFieldError('lastName') ? 'input-error' : 'input'}
                  {...register('lastName', { required: t('common.required'), minLength: { value: 2, message: t('common.required') } })}
                />
                {renderFieldError('lastName')}
              </div>
              <div>
                <label htmlFor="np-firstName" className="label">{t('auth.firstName')} *</label>
                <input
                  id="np-firstName"
                  type="text"
                  autoComplete="off"
                  className={hasFieldError('firstName') ? 'input-error' : 'input'}
                  {...register('firstName', { required: t('common.required'), minLength: { value: 2, message: t('common.required') } })}
                />
                {renderFieldError('firstName')}
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="np-dob" className="label">{t('patient.dateOfBirth')} *</label>
                <input
                  id="np-dob"
                  type="date"
                  max={new Date().toISOString().split('T')[0]}
                  className={hasFieldError('dateOfBirth') ? 'input-error' : 'input'}
                  {...register('dateOfBirth', { required: t('common.required') })}
                />
                {renderFieldError('dateOfBirth')}
              </div>
              <div>
                <label htmlFor="np-gender" className="label">{t('patient.gender')} *</label>
                <select
                  id="np-gender"
                  className={hasFieldError('gender') ? 'input-error' : 'input'}
                  {...register('gender', { required: true })}
                >
                  <option value="MALE">{t('patient.male')}</option>
                  <option value="FEMALE">{t('patient.female')}</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <h2 className="card-title mb-4">{t('doctor.newPatient.contact')}</h2>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="np-phone" className="label">{t('patient.phone')}</label>
              <input
                id="np-phone"
                type="tel"
                autoComplete="off"
                placeholder="+213 / 00213 5XX XXX XXX"
                className={hasFieldError('phone') ? 'input-error' : 'input'}
                {...register('phone')}
              />
              {renderFieldError('phone')}
            </div>

            <div>
              <label htmlFor="np-email" className="label">{t('auth.email')}</label>
              <input
                id="np-email"
                type="email"
                autoComplete="off"
                className={hasFieldError('email') ? 'input-error' : 'input'}
                {...register('email')}
              />
              {renderFieldError('email')}
            </div>
          </div>
        </div>

        <div className="card">
          <h2 className="card-title mb-4">{t('patient.address')}</h2>

          <div className="grid gap-4">
            {!isForeign && (
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="np-wilaya" className="label">{t('patient.wilaya')} *</label>
                  <select
                    id="np-wilaya"
                    className={hasFieldError('wilayaId') ? 'input-error' : 'input'}
                    {...register('wilayaId', {
                      required: !isForeign ? t('common.required') : false,
                      valueAsNumber: true,
                      onChange: () => setValue('communeId', '' as unknown as number),
                    })}
                  >
                    {WILAYAS.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.id}. {w.name}
                      </option>
                    ))}
                  </select>
                  {renderFieldError('wilayaId')}
                </div>
                <div>
                  <label htmlFor="np-commune" className="label">{t('patient.commune')} *</label>
                  <select
                    id="np-commune"
                    className={hasFieldError('communeId') ? 'input-error' : 'input'}
                    {...register('communeId', { required: !isForeign ? t('common.required') : false, valueAsNumber: true })}
                  >
                    <option value="">{t('common.select')}</option>
                    {communes.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  {renderFieldError('communeId')}
                </div>
              </div>
            )}

            <div>
              <label htmlFor="np-address" className="label">
                {t('patient.address')} <span className="font-normal text-gray-400">({t('common.optional')})</span>
              </label>
              <textarea
                id="np-address"
                rows={2}
                className={hasFieldError('address') ? 'input-error' : 'input'}
                {...register('address')}
              />
              {renderFieldError('address')}
            </div>
          </div>
        </div>

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="btn-secondary sm:min-w-32"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="btn-primary sm:min-w-32"
          >
            {createMutation.isPending ? t('common.loading') : t('common.save')}
          </button>
        </div>
      </form>
    </div>
  );
}
